import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ICenter } from "./ICenter";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../../../Components/TextInput/TextInputFieldC";
import Select from "../../../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { centreMasterCreate } from "../../../../Redux/Slices/Booklet/Admin/CentreMaster/CentreMasterCreate";
import { centreMasterUpdate } from "../../../../Redux/Slices/Booklet/Admin/CentreMaster/CentreMasterUpdate";
import { CentreMasterLocation } from "../../../../Redux/Slices/Booklet/Admin/CentreMaster/CentreMatserLocation";

interface CenterFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: Partial<ICenter>;
  setInitialValues: React.Dispatch<React.SetStateAction<Partial<ICenter>>>;
}

interface ILocations {
  id: number;
  state: string;
  region: string;
}

interface LocationObj {
  state: string;
  region: string;
}

const CenterForm: FunctionComponent<CenterFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
}) => {
  const dispatch = useAppDispatch();
  const { data: locationResponse } = useAppSelector(
    (state) => state.CentreMasterLocationSlice
  );

  useEffect(() => {
    dispatch(CentreMasterLocation());
  }, [dispatch]);

  const centerSchema = z.object({
    id: z.number().optional(),

    center_name: z
      .string({
        required_error: "Please enter center name",
        invalid_type_error: "Please enter center name",
      })
      .min(1, { message: "Center name is required" }),

    location: z
      .number({
        required_error: "Please select location",
        invalid_type_error: "Please select location",
      })
      .refine((val) => val > 0, { message: "Please select location" }),

    status: z.boolean({
      required_error: "Please select status",
      invalid_type_error: "Please select status",
    }),
  });

  const methods = useForm<ICenter>({
    defaultValues: {
      center_name: "",
      location: 0,
      status: true,
      ...initialValues,
    },
    resolver: zodResolver(centerSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<ICenter> = (values) => {
    if (values.id) {
      dispatch(centreMasterUpdate(values));
    } else {
      dispatch(centreMasterCreate(values));
    }
  };

  // Transform initialValues.location from object {region, state} to id (number) if needed
  useEffect(() => {
    let transformedValues = { ...initialValues };

    if (
      initialValues.location &&
      typeof initialValues.location === "object" &&
      "state" in initialValues.location &&
      "region" in initialValues.location
    ) {
      const loc = initialValues.location as LocationObj;
      const locations = locationResponse?.data as ILocations[] | undefined;
      if (locations) {
        const matchingLoc = locations.find(
          (l) =>
            l.state === loc.state &&
            l.region === loc.region
        );
        if (matchingLoc) {
          transformedValues.location = matchingLoc.id;
        }
      }
    }

    methods.reset(transformedValues);
  }, [initialValues, methods]);

  useEffect(() => {
    return () => {
      setInitialValues({});
    };
  }, [setInitialValues]);

  const statusOptions: AutocompleteOption[] = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  const locationOptions: AutocompleteOption[] =
    (locationResponse?.data as ILocations[] | undefined)?.map((loc) => ({
      label: `${loc.state} (${loc.region})`,
      value: loc.id,
    })) ?? [];

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        {/* Center Name */}
        <TextInputFieldC
          name="center_name"
          label="Center Name"
          type="text"
          error={!!methods.formState.errors.center_name}
          helperText={methods.formState.errors.center_name?.message}
        />

        {/* Location (Dropdown) */}
        <Select
          name="location"
          label="Location"
          options={locationOptions}
          value={
            locationOptions.find(
              (option) => option.value === methods.getValues("location")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("location", val?.value ?? 0, {
              shouldValidate: true,
            });
          }}
          error={!!methods.formState.errors.location}
          helperText={methods.formState.errors.location?.message}
        />

        {/* Status */}
        <Select
          name="status"
          label="Status"
          options={statusOptions}
          value={
            statusOptions.find(
              (option) => option.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(CenterForm);